import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface CaseStudy {
  id: string;
  industry: string;
  client: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  image: string;
  alt: string;
  testimonial: string;
  author: string;
  role: string;
}

interface CaseStudyShowcaseProps {
  caseStudies: CaseStudy[];
}

const CaseStudyShowcase: React.FC<CaseStudyShowcaseProps> = ({ caseStudies }) => {
  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full mb-4">
              <Icon name="ChartBarIcon" size={20} className="text-success" variant="solid" />
              <span className="text-sm font-cta font-semibold text-success">Proven Results</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
              Industry Success Stories
            </h2>
            <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
              Real challenges solved with measurable outcomes across diverse industries
            </p>
          </div>

          <div className="space-y-8">
            {caseStudies.map((study) => (
              <div
                key={study.id}
                className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden"
              >
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="relative h-64 lg:h-full overflow-hidden">
                    <AppImage
                      src={study.image}
                      alt={study.alt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-2 bg-brand-primary text-white text-sm font-cta font-semibold rounded-lg shadow-card">
                        {study.industry}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 lg:p-8 space-y-6">
                    <div>
                      <h3 className="text-2xl font-headline font-bold text-primary mb-2">{study.client}</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-cta font-semibold text-error mb-2 flex items-center space-x-2">
                            <Icon name="ExclamationCircleIcon" size={16} variant="solid" />
                            <span>Challenge</span>
                          </h4>
                          <p className="text-sm text-muted-foreground font-body">{study.challenge}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-cta font-semibold text-brand-primary mb-2 flex items-center space-x-2">
                            <Icon name="LightBulbIcon" size={16} variant="solid" />
                            <span>Solution</span>
                          </h4>
                          <p className="text-sm text-muted-foreground font-body">{study.solution}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 py-4 border-y border-border">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center">
                          <Icon
                            name={result.icon as any}
                            size={24}
                            className="text-success mx-auto mb-2"
                            variant="solid"
                          />
                          <div className="text-2xl font-headline font-bold text-primary mb-1">{result.value}</div>
                          <div className="text-xs text-muted-foreground font-body">{result.metric}</div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-muted rounded-lg p-4">
                      <p className="text-sm text-foreground font-body italic mb-3">"{study.testimonial}"</p>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                          <Icon name="UserIcon" size={20} className="text-white" variant="solid" />
                        </div>
                        <div>
                          <div className="text-sm font-cta font-semibold text-primary">{study.author}</div>
                          <div className="text-xs text-muted-foreground">{study.role}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyShowcase;